/** One opt-in sample engine for arcade presentations. Only explicit enable
 * creates/resumes audio and fetches same-origin WAVs. Bounded voices and request
 * epochs prevent stale loading, pause, mute or disposal from producing sound. */
export function createSampleAudio({
  samples,
  environment = globalThis,
  onStatus = () => {},
  initialVolume = 0.45,
}) {
  let context = null,
    gain = null,
    enabled = false,
    disposed = false,
    volume = initialVolume,
    ready = false,
    pending = null,
    request = 0;
  const buffers = new Map(),
    voices = new Set(),
    recent = new Map();
  const abort = new AbortController();
  function stop() {
    for (const voice of voices) {
      try {
        voice.stop();
      } catch {
        // A completed source may already have been stopped by the browser.
      }
    }
    voices.clear();
    recent.clear();
  }
  async function enable(value) {
    if (disposed) return false;
    const epoch = ++request;
    enabled = Boolean(value);
    if (!enabled) {
      stop();
      void context?.suspend?.().catch(() => {});
      onStatus('Sound off');
      return false;
    }
    try {
      const Audio = environment.AudioContext || environment.webkitAudioContext;
      if (!Audio) throw new Error('unsupported');
      if (!context) {
        context = new Audio();
        gain = context.createGain();
        gain.gain.value = volume;
        gain.connect(context.destination);
      }
      onStatus('Starting sound…');
      // Resume is invoked in the initiating gesture, before any fetch/decode.
      // Safari can resolve resume while still interrupted; do not claim success.
      let timer;
      try {
        await Promise.race([
          context.resume(),
          new Promise((_, reject) => {
            timer = setTimeout(() => reject(new Error('resume-timeout')), 2000);
          }),
        ]);
      } finally {
        clearTimeout(timer);
      }
      if (disposed || epoch !== request) return false;
      if (context.state !== 'running') throw new Error('interrupted');
      if (!pending)
        pending = Promise.all(
          Object.entries(samples).map(async ([name, url]) => {
            const response = await environment.fetch(url, { signal: abort.signal });
            if (!response.ok) throw new Error('download');
            const buffer = await context.decodeAudioData(await response.arrayBuffer());
            if (!disposed) buffers.set(name, buffer);
          }),
        );
      await pending;
      if (disposed || epoch !== request) return false;
      if (context.state !== 'running') throw new Error('interrupted');
      ready = true;
      onStatus('Sound on');
      return true;
    } catch {
      if (!disposed && epoch === request) {
        enabled = false;
        pending = null;
        ready = false;
        buffers.clear();
        stop();
        onStatus('Sound unavailable · tap to retry');
      }
      return false;
    }
  }
  function play(name, { rate = 1, level = 1, channel = name, interval = 0.04 } = {}) {
    if (
      !enabled ||
      !ready ||
      disposed ||
      context?.state !== 'running' ||
      !buffers.has(name) ||
      voices.size >= 8
    )
      return false;
    const now = context.currentTime;
    if (now - (recent.get(channel) ?? -1) < interval) return false;
    recent.set(channel, now);
    const voice = context.createBufferSource();
    voice.buffer = buffers.get(name);
    voice.playbackRate.value = rate;
    const levelGain = context.createGain();
    levelGain.gain.value = level;
    levelGain.connect(gain);
    voice.connect(levelGain);
    voices.add(voice);
    voice.onended = () => {
      voices.delete(voice);
      voice.disconnect();
      levelGain.disconnect?.();
    };
    voice.start();
    return true;
  }
  return {
    enable,
    play,
    stop,
    resume() {
      if (enabled && context?.state !== 'running') return enable(true);
      return Promise.resolve(enabled && !disposed);
    },
    suspend() {
      request++;
      stop();
      void context?.suspend?.().catch(() => {});
    },
    setVolume(value) {
      volume = Math.max(0, Math.min(1, Number(value) || 0));
      if (gain) gain.gain.value = volume;
    },
    dispose() {
      request++;
      disposed = true;
      enabled = false;
      abort.abort();
      stop();
      buffers.clear();
      void context?.close().catch(() => {});
    },
  };
}
